<template>
  <div class="whiteboard-canvas" ref="canvasContainer" :style="{ width: '100%', height: '100%', minHeight: '350px', backgroundColor: bgColor, overflow: 'hidden', transition: 'background-color 0.2s' }">
    <v-stage :config="stageConfig" ref="stage"
             @mousedown="onMouseDown" @mousemove="onMouseMove" @mouseup="onMouseUp"
             @touchstart="onMouseDown" @touchmove="onMouseMove" @touchend="onMouseUp">
      <v-layer ref="layer">
        <v-group v-for="(item) in shapes" :key="item.id">
          <v-line v-if="item.type==='line'" :config="item.config"/>
          <v-rect v-if="item.type==='rect'" :config="item.config"/>
          <v-circle v-if="item.type==='circle'" :config="item.config"/>
          <v-arrow v-if="item.type==='arrow'" :config="item.config"/>
          <v-text
            v-if="item.type === 'text'"
            :config="item.config"
            @click="($event) => startEditingText($event.target, item.id)"
            @touchend="() => setTimeout(() => startEditingText($refs.stage.getNode().findOne('#' + item.id) || $event.target, item.id), 50)"
          />
          <v-regular-polygon v-if="item.type==='polygon'" :config="item.config"/>
          <v-image v-if="item.type==='image'" :config="item.config" :image="item.imageObj"/>
        </v-group>
      </v-layer>
    </v-stage>
  </div>
</template>

<script>
import { Stage, Layer, Rect, Circle, Arrow, Line, Text, RegularPolygon, Image as KImage } from 'vue-konva';
let uid = 1;

export default {
  name: "WhiteboardCanvas",
  components: { 'v-stage': Stage, 'v-layer': Layer, 'v-rect': Rect, 'v-circle': Circle, 'v-arrow': Arrow, 'v-line': Line, 'v-text': Text, 'v-regular-polygon': RegularPolygon, 'v-image': KImage },
  props: {
    initialDataUrl: { type: String, default: null },
    polygonSides: { type: Number, default: 3 },
    strokeColor: { type: String, default: '#000000' },
    fillColor: { type: String, default: 'transparent' },
    bgColor: { type: String, default: '#ffffff' }
  },
  data() {
    return {
      stageConfig: { width: 911, height: 685 },
      drawing: false,
      currentLinePoints: [],
      startPoint: { x: 0, y: 0 },
      mode: 'select',
      shapes: [], // array of { id, type, config, imageObj }
      history: [],
      historyIndex: -1,
      strokeWidth: 2
    };
  },
  methods: {
    setMode(m) { this.mode = m; },
    pushHistory() {
      try {
        if (this.historyIndex < this.history.length - 1) {
          this.history = this.history.slice(0, this.historyIndex + 1);
        }

        const snapshot = this.shapes.map(s => {
          const cfg = JSON.parse(JSON.stringify(s.config || {}));
          return {
            id: s.id,
            type: s.type,
            config: cfg,
            imageSrc: s.imageObj && s.imageObj.src ? s.imageObj.src : null
          };
        });

        this.history.push(JSON.stringify({ kind: 'shapes_snapshot', shapes: snapshot }));
        this.historyIndex = this.history.length - 1;
      } catch (error) {
        console.error('pushHistory failed:', error);
      }
    },
    undo() {
      if (this.historyIndex <= 0) return;

      this.historyIndex--;
      this.loadFromJSON(this.history[this.historyIndex]);
    },
    redo() {
      if (this.historyIndex >= this.history.length - 1) return;

      this.historyIndex++;
      this.loadFromJSON(this.history[this.historyIndex]);
    },
    loadFromJSON (json) {
      try {
        const parsed = JSON.parse(json);
        if (parsed && parsed.kind === 'shapes_snapshot' && Array.isArray(parsed.shapes)) {
          this.shapes = parsed.shapes.map(item => {
            let imageObj = null;
            if (item.imageSrc) {
              const img = new window.Image();
              img.src = item.imageSrc;
              imageObj = img;
            }
            return {
              id: item.id || (uid++),
              type: item.type,
              config: item.config || {},
              imageObj
            };
          });

          this.$nextTick(() => {
            try { this.redrawLayer(); } catch (error) { console.error('Erro ao redesenhar após loadFromJSON (shapes_snapshot):', error); }
          });
          return;
        }
        const data = parsed;
        this.shapes = [];

        if (data && Array.isArray(data.children)) {
          data.children.forEach(child => this.processNode(child));
        }

        this.$nextTick(() => {
          try { this.redrawLayer(); } catch (error) { console.error('Erro ao redesenhar após loadFromJSON:', error); }
        });
      } catch (error) {
        console.error('loadFromJSON error:', error);
      }
    },
    processNode(node) {
      if (node && Array.isArray(node.children) && node.children.length) {
        node.children.forEach(child => this.processNode(child));
        return;
      }

      if (!node) return;

      const classname = node.className || node.class || '';
      const type = classname.toLowerCase();
      const config = node.attrs || {};
      let imageObj = null;

      if (classname === 'Image') {
        if (config.imageSrc) {
          const img = new window.Image();
          img.src = config.imageSrc;
          imageObj = img;
        }
      }
      this.shapes.push({ id: uid++, type, config, imageObj });
    },
    clear() {
      try {
        this.shapes = [];

        this.$nextTick(() => {
          try {
            if (this.$refs.layer && this.$refs.layer.getNode) {
              const layer = this.$refs.layer.getNode();
              if (layer && layer.destroyChildren) {
                layer.destroyChildren();
                layer.draw();
              }
            }

            if (this.$refs.stage && this.$refs.stage.getNode) {
              const stage = this.$refs.stage.getNode();
              if (stage && stage.draw) stage.draw();
            }
            this.pushHistory();
          } catch (error) {
            console.error('Error while executing $nextTick clear:', error);
          }
        });
      } catch (error) {
        console.error('Error on clear() call:', error);
      }
    },
    export(type = 'png') {
      const stage = this.$refs.stage.getNode();

      if (type === 'svg') {
        const svg = stage.toSVG();
        this.$emit('exported', { type: 'svg', data: svg });
        return;
      }

      const mime = type === 'jpeg' ? 'image/jpeg' : 'image/png';
      const dataURL = stage.toDataURL({ mimeType: mime, quality: 0.92 });
      this.$emit('exported', { type, data: dataURL });
    },
    onMouseDown(e) {
      if (e && e.target !== e.target.getStage()) return;

      const stage = this.$refs.stage && this.$refs.stage.getNode && this.$refs.stage.getNode();
      if (!stage) return;

      if (this.mode === 'draw' || this.mode === 'eraser') {
        this.drawing = true;
        const pos = this.getPointerPos();
        this.currentLinePoints = [pos.x, pos.y];
        const line = {
          id: uid++,
          type: 'line',
          config: {
            points: this.currentLinePoints,
            stroke: this.mode === 'eraser' ? '#000000' : this.strokeColor,
            strokeWidth: this.mode === 'eraser' ? 15 : this.strokeWidth,
            tension: 0.5,
            lineCap: 'round',
            lineJoin: 'round',
            draggable: false,
            globalCompositeOperation: this.mode === 'eraser' ? 'destination-out' : 'source-over'
          }
        };
        this.shapes.push(line);
      } else if (this.mode === 'line') {
        this.drawing = true;
        const pos = this.getPointerPos();
        this.startPoint = { x: pos.x, y: pos.y };
        const line = {
          id: uid++,
          type: 'line',
          config: {
            points: [pos.x, pos.y, pos.x, pos.y],
            stroke: this.strokeColor,
            strokeWidth: this.strokeWidth,
            lineCap: 'round',
            lineJoin: 'round',
            draggable: false
          }
        };
        this.shapes.push(line);
      } else if (this.mode === 'rect' || this.mode === 'circle') {
        this.drawing = true;
        const pos = this.getPointerPos();
        const shape = {
          id: uid++,
          type: this.mode,
          config: {
            x: pos.x, y: pos.y, width: 0, height: 0, radius: 0,
            stroke: this.strokeColor, strokeWidth: this.strokeWidth, fill: this.fillColor
          }
        };
        this.shapes.push(shape);
      } else if (this.mode === 'polygon') {
        this.drawing = true;
        const pos = this.getPointerPos();
        const shape = {
          id: uid++,
          type: 'polygon',
          config: {
            x: pos.x,
            y: pos.y,
            sides: this.polygonSides,
            radius: 0,
            stroke: this.strokeColor,
            strokeWidth: this.strokeWidth,
            fill: this.fillColor
          }
        };
        this.shapes.push(shape);
      } else if (this.mode === 'arrow') {
        this.drawing = true;
        const pos = this.getPointerPos();
        this.startPoint = { x: pos.x, y: pos.y };
        const shape = {
          id: uid++,
          type: this.mode,
          config: {
            points: [pos.x, pos.y, pos.x, pos.y],
            stroke: this.strokeColor,
            strokeWidth: this.strokeWidth,
            fill: this.strokeColor,
            pointerLength: 10,
            pointerWidth: 10,
            draggable: false
          }
        };

        this.shapes.push(shape);
      } else if (this.mode === 'text') {
        const pos = this.getPointerPos();
        const textCfg = {
          id: uid++,
          type: 'text',
          config: {
            x: pos.x,
            y: pos.y,
            text: 'Digite aqui',
            fontSize: 20,
            fontFamily: 'Arial',
            fill: this.strokeColor,
            draggable: true
          }
        };
        this.shapes.push(textCfg);
        this.pushHistory();
      }

      this.$nextTick(() => this.redrawLayer());
    },
    onMouseMove() {
      if (!this.drawing) return;

      const pos = this.getPointerPos();
      const last = this.shapes[this.shapes.length - 1];

      if (!last) return;

      if ((this.mode === 'draw' || this.mode === 'eraser') && last.type === 'line') {
        last.config.points = last.config.points.concat([pos.x, pos.y]);
      } else if (this.mode === 'line' && last.type === 'line') {
        last.config.points = [this.startPoint.x, this.startPoint.y, pos.x, pos.y];
      } else if (last.type === 'rect') {
        last.config.width = Math.abs(pos.x - last.config.x);
        last.config.height = Math.abs(pos.y - last.config.y);
        last.config.x = Math.min(pos.x, last.config.x);
        last.config.y = Math.min(pos.y, last.config.y);
      } else if (last.type === 'circle') {
        const dx = pos.x - last.config.x;
        const dy = pos.y - last.config.y;
        last.config.radius = Math.sqrt(dx * dx + dy * dy);
      } else if (last.type === 'polygon') {
        const dx = pos.x - last.config.x;
        const dy = pos.y - last.config.y;
        last.config.radius = Math.sqrt(dx * dx + dy * dy);
      } else if (last.type === 'arrow') {
        last.config.points = [this.startPoint.x, this.startPoint.y, pos.x, pos.y];
      }

      this.redrawLayer();
    },
    onMouseUp() {
      if (this.drawing) {
        this.drawing = false;
        this.pushHistory();
      }
    },
    redrawLayer() {
      const layer = this.$refs.layer?.getNode();
      if (layer) layer.batchDraw();
    },
    startEditingText(konvaNode, shapeId) {
      if (this._textEditorEl) return;

      const stage = this.$refs.stage.getNode();

      if (!stage) return;

      const absPos = konvaNode.getAbsolutePosition();
      const stageBox = stage.container().getBoundingClientRect();
      const textarea = document.createElement('textarea');
      this._textEditorEl = textarea;

      textarea.value = konvaNode.text() || '';
      textarea.style.position = 'absolute';
      textarea.style.top = `${stageBox.top + absPos.y}px`;
      textarea.style.left = `${stageBox.left + absPos.x}px`;
      textarea.style.width = '200px';
      textarea.style.minWidth = '50px';
      textarea.style.fontSize = (konvaNode.fontSize ? konvaNode.fontSize() : 20) + 'px';
      textarea.style.border = '1px solid #ccc';
      textarea.style.padding = '2px';
      textarea.style.margin = '0';
      textarea.style.background = '#ffffff';
      textarea.style.color = konvaNode.fill ? konvaNode.fill() : '#000';
      textarea.style.outline = 'none';
      textarea.style.resize = 'none';
      textarea.style.lineHeight = konvaNode.lineHeight ? konvaNode.lineHeight() : '1.2';
      textarea.style.fontFamily = konvaNode.fontFamily ? konvaNode.fontFamily() : 'Arial';
      textarea.style.zIndex = 1000;
      textarea.style.overflow = 'hidden';
      textarea.style.whiteSpace = 'pre-wrap';

      stage.container().appendChild(textarea);
      textarea.focus();
      textarea.select();

      const onBlur = () => { this.finishEditingText(textarea, shapeId); };
      const onKeyDown = (e) => {
        if (e.key === 'Escape') { this.cancelTextEditing(); e.preventDefault(); return; }
        if (e.key === 'Enter' && !e.shiftKey) { this.finishEditingText(textarea, shapeId); e.preventDefault(); }
      };

      textarea.addEventListener('blur', onBlur);
      textarea.addEventListener('keydown', onKeyDown);

      this._textEditorHandlers = { onBlur, onKeyDown };
    },
    finishEditingText(textarea, shapeId) {
      if (!textarea) return;
      const newText = textarea.value;

      const idx = this.shapes.findIndex(s => String(s.id) === String(shapeId));
      if (idx !== -1) this.shapes[idx].config.text = newText;

      this._cleanupTextEditor();

      this.$nextTick(() => {
        try { this.redrawLayer(); } catch (e) { console.error(e); }
        try { this.pushHistory(); } catch (e) { console.error('pushHistory failed:', e); }
      });
    },
    cancelTextEditing() { this._cleanupTextEditor(); },
    _cleanupTextEditor() {
      if (!this._textEditorEl) return;
      const el = this._textEditorEl;
      if (this._textEditorHandlers) {
        el.removeEventListener('blur', this._textEditorHandlers.onBlur);
        el.removeEventListener('keydown', this._textEditorHandlers.onKeyDown);
        this._textEditorHandlers = null;
      }
      if (el.parentNode) el.parentNode.removeChild(el);
      this._textEditorEl = null;
    },
    getPointerPos() {
      const stage = this.$refs.stage.getNode();
      const pointer = stage.getPointerPosition();

      return pointer || { x: 0, y: 0 };
    },
    loadInitialDataUrl(dataUrl) {
      if (!dataUrl) return;
      if (dataUrl.startsWith('data:')) {
        this._loadImageFromDataUrl(dataUrl);
        return;
      }
      this._loadImageFromUrl(dataUrl);
    },
    _loadImageFromDataUrl(dataUrl) {
      const img = new window.Image();
      img.onload = () => {
        const config = { x: 0, y: 0, width: this.width, height: this.height, draggable: false, listening: false, imageSrc: img.src };
        this.shapes.push({ id: uid++, type: 'image', config, imageObj: img });
        this.redrawLayer();
        this.pushHistory();
      };
      img.onerror = () => {
        console.error('Failed to load image from dataURL:', dataUrl.slice(0, 100));
      };
      img.src = dataUrl;
    },
    _loadImageFromUrl(url) {
      fetch(url, { credentials: 'include' })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch image: ' + response.status);
          }
          return response.blob();
        })
        .then(blob => {
          const reader = new FileReader();
          reader.onload = (e) => {
            this._loadImageFromDataUrl(e.target.result);
          };
          reader.onerror = () => {
            console.error('Failed to read image blob as dataURL');
          };
          reader.readAsDataURL(blob);
        })
        .catch(err => {
          console.error('Failed to load image from URL:', url, err);
        });
    },
    toDataURL(mime = 'image/png') {
      return this.$refs.stage.getNode().toDataURL({ mimeType: mime, quality: 0.92 });
    },
    fitStageIntoParentContainer() {
      const container = this.$refs.canvasContainer;
      if (!container) return;
      const containerWidth = window.innerWidth < 768 ? container.offsetWidth : 911;
      const containerHeight = window.innerWidth < 768 ? window.innerHeight * 0.5 : 685;

      this.stageConfig.width = containerWidth;
      this.stageConfig.height = containerHeight;

      if (this.$refs.stage) {
        const stageNode = this.$refs.stage.getNode();
        stageNode.width(containerWidth);
        stageNode.height(containerHeight);
        stageNode.batchDraw();
      }
    },
  },
  watch: {
    initialDataUrl(newVal) {
      if (!newVal) this.clear();
      else this.loadInitialDataUrl(newVal);
    }
  },
  mounted() {
    window.addEventListener('unhandledrejection', function (event) {
      console.error('Unhandled rejection (global):', event.reason);
      if (event.reason && typeof event.reason === 'object') {
        console.error('reason stack/props:', event.reason.stack || event.reason);
      }
    })

    this.pushHistory();
    this.$nextTick(() => this.fitStageIntoParentContainer());
    window.addEventListener('resize', this.fitStageIntoParentContainer);

    if (this.initialDataUrl) this.loadInitialDataUrl(this.initialDataUrl);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.fitStageIntoParentContainer);
  }
}
</script>
